import { Box, TextField } from '@mui/material'
import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Emc } from 'src/Libs/flow/installation/forms'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { isFlowTheme } from 'src/Libs/utils'
import { QrPairingEditor, QrScanButton } from 'src/Libs/installations'

/** Allow only alphanumeric characters and the - symbol. Also allows an empty string. */
const PAIRING_ID_REGEX = /^([a-z0-9]|-)*$/i

// #region Dialog Component

export interface EmcPairingDialogProps {
  submitPairingId: (id: string, setError?: (err?: string) => void) => void | boolean | Promise<void | boolean>
  emc?: Emc
  pairingId?: string
  isNewEmc?: boolean
  onClose?: () => void
  useDefaultValidation?: boolean
  customTitle?: string
  customInputLabel?: string
  onScanQrCode?: (
    data: string | null,
    setPairingId: (value: string) => void,
    setStartQrScanner: (value: boolean) => void,
    setScannerError: (value: string) => void
  ) => void
}

// TODO: test with real device
export const EmcPairingDialog: React.FC<EmcPairingDialogProps> = ({
  emc,
  pairingId: inputPairingId,
  isNewEmc: inputIsNewEmc,
  submitPairingId,
  onClose,
  useDefaultValidation = true,
  onScanQrCode,
  customTitle,
  customInputLabel,
}) => {
  const { t } = useCustomTranslation()
  const initialPairingId = emc?.pairingId ?? inputPairingId ?? ''
  const isNewEmc = emc?.isNew ?? inputIsNewEmc ?? false
  const [pairingId, setPairingId] = useState(initialPairingId)

  const [customError, setCustomError] = useState<string | undefined>()

  const pairingIdContainsSpecialCharacters = !PAIRING_ID_REGEX.test(pairingId)

  const error =
    useDefaultValidation && pairingIdContainsSpecialCharacters ? t('installation.productEditor.pairingEditor.no-special-characters') : undefined

  const isSame = initialPairingId === pairingId

  const handleSubmit = async (): Promise<void> => {
    /** Dashes are allow during typing, but must be removed on submition. */
    const shouldClose = await submitPairingId(pairingId.replaceAll('-', ''), setCustomError)

    // TODO: if device exists, pair and save (replace if a previous id exists).
    // If not, just set the pairingId and handle it on installation save.
    // Reference functions
    // handlePairingSave, ConfigurationEditor:1452
    // onReplaceDevice, ConfigurationEditor:1052

    if (shouldClose === false) return
    onClose?.()
  }

  const [startQrScanner, setStartQrScanner] = useState(false)

  const handleOnClose = (): void => {
    onClose?.()
  }

  const dialogActions = useActions(isNewEmc, handleSubmit, !!error || isSame, handleOnClose)

  return (
    <Dialog
      title={customTitle ?? t('installation.productEditor.pairingEditor.title.emc')}
      maxWidth="md"
      onClose={handleOnClose}
      actions={dialogActions}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, py: 2 }}>
        <TextField
          name="Emc pairing id"
          label={customInputLabel ?? t('productEditor.pairingEditor.pairingID.emc')}
          fullWidth
          value={pairingId}
          onChange={e => setPairingId(e.target.value)}
          inputProps={{
            maxLength: 50,
          }}
          error={!!error || !!customError}
          helperText={error ?? customError}
        />

        <QrScanButton onClick={() => setStartQrScanner(!startQrScanner)} color={isFlowTheme ? undefined : 'primary'} />
      </Box>

      <QrPairingEditor startQrScanner={startQrScanner} setStartQrScanner={setStartQrScanner} setPairingId={setPairingId} onScan={onScanQrCode} />
    </Dialog>
  )
}

// #region Actions

const useActions = (isNewEmc: boolean, onPrimaryAction: () => void, isPrimaryDisabled: boolean, onSecondaryAction: () => void): DialogActions => {
  const { t } = useCustomTranslation()
  const primaryText = isNewEmc ? t('installation.productEditor.pairingEditor.save') : t('installation.productEditor.pairingEditor.pairAndSave')

  return {
    secondary: {
      name: t('common.cancel'),
      onClick: onSecondaryAction,
    },
    primary: {
      name: primaryText,
      onClick: onPrimaryAction,
      disabled: isPrimaryDisabled,
    },
  }
}
